import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterBar, FilterSelect, FilterSearch } from '@/components/feature/FilterBar';
import PaperPreviewModal from './components/PaperPreviewModal';

type Board = 'AQA' | 'OCR' | 'Edexcel' | 'WJEC';
type Year = string;

interface PastPaper {
  id: string;
  board: Board;
  year: Year;
  paper: string;
  title: string;
  type: string;
  pages: number;
  size: string;
  fileUrl: string;
}

const boards: Board[] = ['AQA', 'OCR', 'Edexcel', 'WJEC'];
const years = ['2024', '2023', '2022', '2021', '2020', '2019', '2018'];

const boardColors: Record<string, string> = {
  AQA: 'bg-rose-100 text-rose-700 border-rose-200',
  OCR: 'bg-teal-100 text-teal-700 border-teal-200',
  Edexcel: 'bg-amber-100 text-amber-700 border-amber-200',
  WJEC: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

const typeColors: Record<string, string> = {
  'Question Paper': 'bg-slate-100 text-slate-700',
  'Mark Scheme': 'bg-emerald-100 text-emerald-700',
  'Examiner Report': 'bg-violet-100 text-violet-700',
};

const typeIcons: Record<string, string> = {
  'Question Paper': 'ri-file-text-line',
  'Mark Scheme': 'ri-check-double-line',
  'Examiner Report': 'ri-bar-chart-line',
};

export default function GcsePastPapersPage() {
  const [pastPapers, setPastPapers] = useState<PastPaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoard, setSelectedBoard] = useState<Board | 'All'>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [selectedType, setSelectedType] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewPaper, setPreviewPaper] = useState<PastPaper | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/pastpapers`);
        if (res.ok) {
          const data = await res.json();
          // Filter for GCSE level only
          const gcse = data.filter((p: any) => p.level === 'GCSE').map((p: any) => ({
            id: p.id || p._id,
            board: p.board,
            year: p.year?.toString() || 'N/A',
            paper: p.tags?.split(',')[0] || 'Paper', // Fallback
            title: p.title,
            type: p.type,
            pages: p.pages || 0,
            size: p.size || '0.1 MB',
            fileUrl: p.fileUrl
          }));
          setPastPapers(gcse);
        }
      } catch (err) {
        console.error('Failed to fetch past papers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPapers();
  }, [API_BASE]);

  const filtered = pastPapers.filter((p) => {
    if (selectedBoard !== 'All' && p.board !== selectedBoard) return false;
    if (selectedYear !== 'All' && p.year !== selectedYear) return false;
    if (selectedType !== 'All' && p.type !== selectedType) return false;
    if (searchQuery && !p.title.toLowerCase().includes(searchQuery.toLowerCase()) && !p.paper.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const grouped = filtered.reduce<Record<string, PastPaper[]>>((acc, p) => {
    const key = `${p.board} ${p.year}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20stacked%20paper%20documents%20and%20exam%20sheets%2C%20deep%20navy%20gradient%2C%20geometric%20patterns%2C%20clean%20minimal%20design%2C%20subtle%20grid%20overlay%2C%20professional%20academic%20atmosphere&width=1440&height=280&seq=gcsepapers1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">GCSE Resources</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Past Papers</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            GCSE Past Papers
          </h1>
          <p className="text-slate-400 text-base max-w-2xl">
            Access past exam papers and mark schemes for all major GCSE Computer Science exam boards. Filter by board, year and paper type.
          </p>
        </div>
      </section>

      {/* Filters */}
      <FilterBar count={filtered.length} countLabel="papers">
        <FilterSelect
          label="Board"
          value={selectedBoard}
          onChange={(v) => setSelectedBoard(v as Board | 'All')}
          options={[{ value: 'All', label: 'All Boards' }, ...boards.map((b) => ({ value: b, label: b }))]}
        />
        <FilterSelect
          label="Year"
          value={selectedYear}
          onChange={(v) => setSelectedYear(v as Year | 'All')}
          options={[{ value: 'All', label: 'All Years' }, ...years.map((y) => ({ value: y, label: y }))]}
        />
        <FilterSelect
          label="Type"
          value={selectedType}
          onChange={(v) => setSelectedType(v)}
          options={[
            { value: 'All', label: 'All Types' },
            { value: 'Question Paper', label: 'Question Paper' },
            { value: 'Mark Scheme', label: 'Mark Scheme' },
            { value: 'Examiner Report', label: 'Examiner Report' },
          ]}
        />
        <div className="sm:ml-auto">
          <FilterSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search papers..."
            width="sm:w-56"
          />
        </div>
      </FilterBar>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">
        {Object.keys(grouped).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-file-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No papers found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {Object.entries(grouped).map(([groupKey, papers]) => {
              const board = papers[0].board;
              return (
                <div key={groupKey}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${boardColors[board]}`}>{board}</span>
                    <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>{groupKey}</h2>
                    <span className="text-sm text-slate-400">{papers.length} file{papers.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {papers.map((paper) => (
                      <div key={paper.id} className="bg-white rounded-xl border border-slate-100 p-5 hover:border-emerald-200 transition-colors group">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-lg flex-shrink-0 group-hover:bg-emerald-50 transition-colors">
                            <i className={`${typeIcons[paper.type]} text-slate-500 group-hover:text-emerald-600 text-lg transition-colors`}></i>
                          </div>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${typeColors[paper.type]}`}>{paper.type}</span>
                        </div>
                        <p className="text-xs font-semibold text-slate-500 mb-1">{paper.paper}</p>
                        <h3 className="text-sm font-bold text-slate-800 mb-3 leading-snug">{paper.title}</h3>
                        <div className="flex items-center gap-3 text-xs text-slate-400 mb-4">
                          <span className="flex items-center gap-1"><i className="ri-pages-line"></i>{paper.pages} pages</span>
                          <span className="flex items-center gap-1"><i className="ri-file-line"></i>{paper.size}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setPreviewPaper(paper)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-eye-line text-sm"></i>
                            Preview
                          </button>
                          <button className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap">
                            <i className="ri-download-line text-sm"></i>
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <PaperPreviewModal
        paper={previewPaper}
        onClose={() => setPreviewPaper(null)}
        typeColors={typeColors}
        typeIcons={typeIcons}
        boardBadgeColor={previewPaper ? boardColors[previewPaper.board] : ''}
      />

      <Footer />
    </div>
  );
}
